import { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';

const WS_BASE = process.env.REACT_APP_BACKEND_URL
  ?.replace('https://', 'wss://')
  .replace('http://', 'ws://');

export const useWebSocketNotifications = (onNotification) => {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const [connected, setConnected] = useState(false);

  const getSessionToken = useCallback(() => {
    const cookies = document.cookie.split(';');
    const sessionCookie = cookies.find(c => c.trim().startsWith('session_token='));
    return sessionCookie ? sessionCookie.split('=')[1].trim() : null;
  }, []);

  const connect = useCallback(() => {
    const token = getSessionToken();
    if (!token || !WS_BASE) return;

    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_BASE}/api/ws/notifications?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      const ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 30000);
      ws._pingInterval = ping;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'notification' && msg.data) {
          const notif = msg.data;
          toast(notif.title || 'Notification', {
            description: notif.message,
            duration: 5000,
          });
          if (onNotification) onNotification(notif);
        }
      } catch (e) {
        // ignore non-json (pong etc)
      }
    };

    ws.onclose = () => {
      setConnected(false);
      if (ws._pingInterval) clearInterval(ws._pingInterval);
      reconnectTimer.current = setTimeout(() => connect(), 5000);
    };

    ws.onerror = () => ws.close();
  }, [getSessionToken, onNotification]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { wsRef, connected };
};
